import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, withStyles } from '@material-ui/core';
import { Modal } from '@orijinworks/frontend-commons';
import styles from './bulk-link-confirm-modal.style';
import ScoreBadge from '../score-badge/score-badge';
import { getSuggestionScoreTier } from '../../utils/lms-course-review.utils';
import { SCORE_TIER } from '../../constants/score-tiers.constants';

const tierCount = (items, tier) =>
  items.filter((item) => getSuggestionScoreTier(item.suggestedResource?.score) === tier).length;

const BulkLinkConfirmModal = ({
  classes,
  modalRef,
  items,
  loading,
  onClose,
  onConfirm,
}) => {
  const count = items.length;
  const highCount = tierCount(items, SCORE_TIER.HIGH);
  const mediumCount = tierCount(items, SCORE_TIER.MEDIUM);
  const lowCount = tierCount(items, SCORE_TIER.LOW);
  const itemNoun = count === 1 ? 'item' : 'items';
  const linkActionText = loading ? 'Linking…' : `Link ${count} ${itemNoun}`;

  return (
    <Modal
      ref={modalRef}
      id="lms-course-review-bulk-link-modal"
      cancelBtnText="Cancel"
      actionBtnText={linkActionText}
      showCrossIcon={false}
      labelledBy="bulk-link-title"
      describedBy="bulk-link-description"
      onClose={onClose}
      onContinue={onConfirm}
      modalType={{ type: 'confirmation', status: 'info', size: 'medium' }}
      styles={{ mainWrapperStyles: { height: 'auto', maxHeight: '85vh' } }}
      data-testid="bulk-link-confirm-modal"
    >
      <Typography id="bulk-link-title" variant="h6" gutterBottom>
        Link {count} queue item{count === 1 ? '' : 's'} using suggestions?
      </Typography>
      <Typography id="bulk-link-description" variant="body2" color="textSecondary" paragraph>
        Each selected row will be linked to its top suggested course. This cannot be undone
        from the queue UI.
      </Typography>
      {(highCount > 0 || mediumCount > 0 || lowCount > 0) && (
        <Typography variant="caption" color="textSecondary" className={classes.tierSummary}>
          {highCount > 0 ? `${highCount} high (≥90%)` : null}
          {highCount > 0 && (mediumCount > 0 || lowCount > 0) ? ' · ' : null}
          {mediumCount > 0 ? `${mediumCount} medium (70–89%)` : null}
          {mediumCount > 0 && lowCount > 0 ? ' · ' : null}
          {lowCount > 0 ? `${lowCount} low (<70%)` : null}
        </Typography>
      )}
      {lowCount > 0 && (
        <Box className={classes.warning} data-testid="bulk-link-low-confidence-warning">
          <Typography variant="body2" className={classes.warningText}>
            {lowCount} selected item{lowCount === 1 ? '' : 's'} have low confidence (&lt;70%).
            Review carefully before bulk linking.
          </Typography>
        </Box>
      )}
      <Box className={classes.list} data-testid="bulk-link-preview-list">
        {items.map((item) => {
          const tier = getSuggestionScoreTier(item.suggestedResource?.score);
          const isLowConfidence = tier === SCORE_TIER.LOW;
          const itemClassName = isLowConfidence
            ? `${classes.listItem} ${classes.listItemLow}`
            : classes.listItem;

          return (
            <Box
              key={item.id}
              className={itemClassName}
            >
            <Box className={classes.courseBlock}>
              <Typography variant="body2" className={classes.courseName}>
                {item.canvasCourseName}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                → {item.suggestedResource?.name}
              </Typography>
            </Box>
            <ScoreBadge score={item.suggestedResource?.score} />
          </Box>
          );
        })}
      </Box>
    </Modal>
  );
};

BulkLinkConfirmModal.propTypes = {
  modalRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.object }),
  ]).isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

BulkLinkConfirmModal.defaultProps = {
  loading: false,
};

export default withStyles(styles)(BulkLinkConfirmModal);
